import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users, ArrowRight, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import EntityPicker from "../components/EntityPicker";
import { toast } from "sonner";
import { adminApi } from "../api";

export default function AdminChangeParent() {
  const [clientId, setClientId] = useState("");
  const [newParentId, setNewParentId] = useState("");
  const [current, setCurrent] = useState(null);
  const [newParent, setNewParent] = useState(null);
  const [err, setErr] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Fetch current client details (including current parent) whenever clientId changes
  useEffect(() => {
    setCurrent(null); setErr("");
    if (!clientId) return;
    let cancelled = false;
    adminApi.opsClientParent(clientId)
      .then((r) => { if (!cancelled) setCurrent(r.data); })
      .catch((e) => { if (!cancelled) setErr(e.message || "Erro"); });
    return () => { cancelled = true; };
  }, [clientId]);

  const canSubmit = clientId && newParentId && String(clientId) !== String(newParentId) && !submitting;

  const doSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setErr(""); setResult(null);
    try {
      const r = await adminApi.opsChangeParent({
        id_cliente: Number(clientId),
        new_parent_id: Number(newParentId),
      });
      setResult(r);
      setConfirmOpen(false);
    } catch (e) {
      const msg = e.message || "Erro";
      setErr(msg);
      toast.error(msg, { description: "Mudar Pai de Cliente" });
      setConfirmOpen(false);
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="admin-change-parent" className="max-w-3xl">
      <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-[#978D78] hover:text-[#0F1829] mb-3">
        <ArrowLeft size={13} /> Voltar ao dashboard
      </Link>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-11 h-11 grid place-items-center rounded-xl bg-[#0F1829] text-white"><Users size={22} /></div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Mudar Pai de Cliente</h1>
          <p className="text-sm text-[#4B5563]">Reorganiza o upline de um cliente e actualiza a EstruturaRede em cascata.</p>
        </div>
      </div>

      <div className="bg-white border border-[#E6E1D6] rounded-md p-5 space-y-5">
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold">Cliente a mover</label>
          <div className="mt-1.5">
            <EntityPicker entity="cliente" value={clientId} onChange={setClientId}
              placeholder="Nome ou email do cliente..." testid="picker-client" />
          </div>
          {current && (
            <div className="mt-2 text-xs bg-[#FAF8F2] border border-[#E6E1D6] rounded p-3">
              <div><strong>{current.NomeCompleto}</strong> ({current.Email})</div>
              <div className="text-[#6B7280]">Pai actual:
                <span className="ml-1 font-mono">#{current.ID_ClienteRecomendacao ?? "—"}</span>
                {current.ParentNome && <span className="ml-1">· {current.ParentNome}</span>}
              </div>
              <div className="text-[#6B7280]">Estrutura: <span className="font-mono">{current.EstruturaRede || "—"}</span></div>
            </div>
          )}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#978D78] font-bold">Novo pai</label>
          <div className="mt-1.5">
            <EntityPicker entity="cliente" value={newParentId}
              onChange={setNewParentId} onPick={(o) => setNewParent(o)}
              placeholder="Nome ou email do novo pai..." testid="picker-parent" />
          </div>
        </div>

        {err && (
          <div className="px-3 py-2 bg-red-50 border border-red-200 text-sm text-red-700 rounded flex items-start gap-2">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" /><span>{err}</span>
          </div>
        )}

        {result && (
          <div className="px-3 py-2 bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 rounded flex items-start gap-2" data-testid="change-parent-success">
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
            <span>Pai alterado com sucesso. Nova estrutura: <span className="font-mono">{result.new_estrutura}</span></span>
          </div>
        )}

        <div className="flex justify-end">
          <button onClick={() => setConfirmOpen(true)} disabled={!canSubmit}
            data-testid="submit-change-parent"
            className="px-4 py-2 bg-[#0F1829] text-white text-sm rounded disabled:opacity-40 hover:bg-black transition inline-flex items-center gap-2">
            Confirmar mudança <ArrowRight size={14} />
          </button>
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={() => !submitting && setConfirmOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-lg shadow-2xl w-full max-w-xl p-5" data-testid="confirm-modal">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle size={18} className="text-amber-600" />
              <h2 className="text-lg font-bold">Confirmar mudança de pai</h2>
            </div>
            <div className="text-sm space-y-2 bg-[#FAF8F2] border border-[#E6E1D6] rounded p-3 mb-4">
              <div><strong>Cliente:</strong> #{clientId} · {current?.NomeCompleto}</div>
              <div><strong>Pai antigo:</strong> #{current?.ID_ClienteRecomendacao ?? "—"} · {current?.ParentNome || "—"}</div>
              <div><strong>Novo pai:</strong> #{newParentId} · {newParent?.label || ""}</div>
            </div>
            <p className="text-xs text-[#6B7280] mb-4">A EstruturaRede do cliente e de todos os descendentes vai ser actualizada. Esta acção fica registada em auditoria.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmOpen(false)} disabled={submitting}
                className="px-4 py-2 text-sm border border-[#E6E1D6] rounded hover:bg-[#F5F2EB]">Cancelar</button>
              <button onClick={doSubmit} disabled={submitting}
                data-testid="confirm-submit"
                className="px-4 py-2 bg-[#0F1829] text-white text-sm rounded disabled:opacity-40 inline-flex items-center gap-2">
                {submitting && <Loader2 size={14} className="animate-spin" />} Aplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
